// Campeonatos (lote 7, itens 7 e 8): ligas e campeonatos criados pelo Ouro, soltos ou dentro de uma comunidade. Na aba
// Ligas, ao lado da Liga da Semana e das Comunidades: os seus e os do seu trio, com "Criar campeonato" (cadeado fora do
// Ouro). A página do campeonato (/campeonato/<slug>) abre sem conta: tabela, chave, jogos, trios e, para o organizador,
// Organizar (começar, janelas, hora de cada jogo, remarcar e resultado à mão, sempre com motivo) e as ferramentas de
// organizador do Ouro (divulgar, chamar amigos, avisos, transmissão, página pública e exportar).
import { useEffect, useState } from 'react';
import {
  ATRASO_DA_TRANSMISSAO_S, AVISO_DO_PREMIO, AVISO_MAX, AVISOS_POR_DIA, CAMPEONATOS_ATIVOS_POR_CONTA, CONVITES_POR_DIA, DIAS_POR_RODADA_MAX, DIAS_POR_RODADA_MIN, type Formato, MINUTOS_PARA_WO, MOTIVO_MAX,
  NOME_DO_CAMPEONATO_MAX, NOMES_DOS_FORMATOS, PREMIO_MAX, TEMPOS_DE_JOGADA_MS, TRIOS_MAX, TRIOS_MIN, conferirQuantidade,
} from '@truco6/campeonatos';
import { NOME_DO_PLANO, pode } from '@truco6/planos';
import { ofensivo } from '@truco6/moderacao';
import type { CampeonatoDaSala } from '@truco6/mesa/protocolo';
import { avisarNaTela } from '../comum/aviso.tsx';
import {
  type FichaCampeonato, type JogoNaTela, type MeusCampeonatos, type ResumoCampeonato, type Sinais, type TrioNaTela, avisarInscritos, baixarCsv, campeonatosDaComunidade,
  cancelarCampeonato, comecarCampeonato, convidarParaCampeonato, criarCampeonato, decidirInscricao, diaDoJogo, editarCampeonato, exportarCampeonato, inscreverTrio,
  lancarResultado, lerCampeonato, linkDoCampeonato, marcarJogo, meusCampeonatos, mudarJanela, nomeDoLado, quandoDoJogo,
} from '../comum/campeonatos.ts';
import { type AmigoNaLista, lerAmigos } from '../comum/amigos.ts';
import { IconeAmigos } from '../comum/BotaoAmizade.tsx';
import { instanteDe } from '../comum/comunidades.ts';
import { conta, type PerfilConta, type Sessao } from '../comum/conta.ts';
import { guardarDestino } from '../comum/chegada.ts';
import { medir, medirUmaVez } from '../comum/medicao.ts';
import { type AbaDoCampeonato, ir, voltar } from '../comum/rotas.ts';
import { semPartidasAgora } from '../comum/limite.ts';
import { Rosto } from '../comum/Rosto.tsx';
import { salaDoCampeonato } from '../mesa/criar.ts';
import { VISIBILIDADE_DOS_SINAIS } from '../mesa/textos.ts';
import { Qr } from '../telas/Marcador.tsx';
import { Carregando, Tela, Topo } from '../telas/comum.tsx';
import { AbasDasLigas, Chave, Folha, Opcoes } from './comunidades.tsx';
import { Escudo } from './trios.tsx';

const ESTADO: Record<FichaCampeonato['estado'], string> = { inscricao: 'Inscrições abertas', andamento: 'Em andamento', concluido: 'Concluído', cancelado: 'Cancelado' };
const DIAS = [1, 2, 3, 5, 7, 10, 14, 21, 30].filter((d) => d >= DIAS_POR_RODADA_MIN && d <= DIAS_POR_RODADA_MAX);
const textoOfensivo = (t: string) => t.trim() !== '' && ofensivo(t);
const hojeEmBrasilia = (mais = 0) => new Date(Date.now() + mais - 3 * 3_600_000).toISOString().slice(0, 10);
const grupoDe = (g: number) => `Grupo ${String.fromCharCode(65 + g)}`;

function Trofeu({ tamanho = 56 }: { tamanho?: number }) {
  return <span className="trofeu" style={{ width: tamanho, height: tamanho, fontSize: tamanho * 0.5 }} aria-hidden="true">🏆</span>;
}

// A faixa da sala de campeonato, no lugar do código da sala: o campeonato, a rodada e o confronto.
export function FaixaCampeonato({ c }: { c: CampeonatoDaSala }) {
  return (
    <header className="faixa-campeonato" aria-label={`${c.nome}, ${c.rodada}: ${c.trios[0]} contra ${c.trios[1]}`}>
      <Trofeu tamanho={40} />
      <p><b>{c.nome}</b><span>{c.rodada} · {c.trios[0]} x {c.trios[1]}{c.melhorDe === 3 ? ` · melhor de 3 (${c.vitorias[0]} x ${c.vitorias[1]})` : ''}</span></p>
    </header>
  );
}

// Quem venceu o confronto, com ele decidido.
export const vencedorDoConfronto = (c: CampeonatoDaSala): string | null => (c.decidido ? c.trios[c.vitorias[0] > c.vitorias[1] ? 0 : 1] : null);

function linhaDoResumo(c: ResumoCampeonato): string {
  const partes = [NOMES_DOS_FORMATOS[c.formato], `${c.trios} ${c.trios === 1 ? 'trio' : 'trios'}`];
  if (c.estado === 'inscricao') partes.push(`inscrições até ${diaDoJogo(c.inscricaoAte)}`);
  if (c.estado === 'concluido' && c.campeao) partes.push(`campeão: ${c.campeao}`);
  return partes.join(' · ');
}

function LinhaCampeonato({ c }: { c: ResumoCampeonato }) {
  return (
    <button className="cartao linha-trio linha-campeonato" onClick={() => ir({ tela: 'campeonato', slug: c.slug })}>
      <Trofeu tamanho={44} />
      <span className="quem"><b>{c.nome}</b><small>{linhaDoResumo(c)}</small></span>
      <span className="etiquetas">
        <span className={'etiqueta estado-' + c.estado}>{ESTADO[c.estado]}</span>
        {c.organizador && <span className="etiqueta capitao">Organizador</span>}
      </span>
    </button>
  );
}

// A aba Campeonatos da aba Ligas: os que você organiza, os do seu trio e o Criar.
export function CampeonatosTela({ sessao, perfil }: { sessao: Sessao; perfil: PerfilConta }) {
  const [m, setM] = useState<MeusCampeonatos | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  useEffect(() => {
    let vivo = true;
    meusCampeonatos(sessao).then((x) => { if (vivo) setM(x); }, (e: Error) => { if (vivo) setErro(e.message); });
    return () => { vivo = false; };
  }, [sessao.id]);
  const ouro = pode(perfil.plano, 'criarLiga');
  return (
    <Tela larga>
      {/* Campeonatos é uma aba de Ligas, que é aba principal: sem o voltar. */}
      <Topo titulo="Campeonatos" />
      <AbasDasLigas atual="campeonatos" />
      <div className="campeonatos">
        <p className="sub">Pontos corridos, mata-mata ou grupos, com trios fixos. O organizador marca os jogos e a tabela anda sozinha.</p>
        {erro && <p className="erro" role="alert">{erro}</p>}
        {!m && !erro && <p className="carregando" role="status">Carregando...</p>}
        {/* A partir de 720 px, os campeonatos do seu trio e os que você organiza vão para a coluna da direita. */}
        <div className="lista-principal">
        {m && (
          <section aria-labelledby="h-do-trio">
            <h2 id="h-do-trio" className="rotulo">Do seu trio{m.inscritos.length ? ` · ${m.inscritos.length}` : ''}</h2>
            {m.inscritos.length === 0
              ? <p className="cartao vazio-lista">Seu trio ainda não está em nenhum campeonato. O capitão inscreve o trio pelo link do campeonato ou dentro da comunidade.</p>
              : <ul className="lista-trios">{m.inscritos.map((c) => <li key={c.id}><LinhaCampeonato c={c} /></li>)}</ul>}
          </section>
        )}
        {m && m.organizados.length > 0 && (
          <section aria-labelledby="h-organizados">
            <h2 id="h-organizados" className="rotulo">Que você organiza · {m.organizados.length}</h2>
            <ul className="lista-trios">{m.organizados.map((c) => <li key={c.id}><LinhaCampeonato c={c} /></li>)}</ul>
          </section>
        )}
        </div>
        {m && (m.podeCriar
          ? <button className="btn1 largo" onClick={() => ir({ tela: 'novo-campeonato' })}>CRIAR CAMPEONATO</button>
          : (
            <div className="cadeado-tentos">
              <p className="cadeado">{ouro ? `O plano Ouro organiza até ${CAMPEONATOS_ATIVOS_POR_CONTA} campeonatos ao mesmo tempo.` : 'Criar campeonato é do plano Ouro.'}</p>
              {!ouro && <button className="btn2 dourado" onClick={() => ir({ tela: 'planos', plano: 'ouro', ciclo: 'mensal' })}>Ver planos</button>}
            </div>
          ))}
      </div>
    </Tela>
  );
}

// A aba Campeonatos da comunidade.
export function CampeonatosDaComunidade({ sessao, comunidade }: { sessao: Sessao; comunidade: string }) {
  const [r, setR] = useState<{ campeonatos: ResumoCampeonato[]; podeCriar: boolean } | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  useEffect(() => {
    let vivo = true;
    campeonatosDaComunidade(sessao, comunidade).then((x) => { if (vivo) setR(x); }, (e: Error) => { if (vivo) setErro(e.message); });
    return () => { vivo = false; };
  }, [comunidade]);
  return (
    <section aria-label="Campeonatos da comunidade">
      {r?.podeCriar && <button className="btn1 largo" onClick={() => ir({ tela: 'novo-campeonato', comunidade })}>CRIAR CAMPEONATO</button>}
      {erro && <p className="erro" role="alert">{erro}</p>}
      {!r && !erro && <p className="carregando" role="status">Carregando os campeonatos...</p>}
      {r && r.campeonatos.length === 0 && <p className="cartao vazio-lista">Nenhum campeonato na comunidade ainda. O dono e os moderadores com Ouro criam.</p>}
      {r && r.campeonatos.length > 0 && <ul className="lista-trios">{r.campeonatos.map((c) => <li key={c.id}><LinhaCampeonato c={c} /></li>)}</ul>}
    </section>
  );
}

export function CriarCampeonatoTela({ sessao, perfil, comunidade = null }: { sessao: Sessao; perfil: PerfilConta; comunidade?: string | null }) {
  const [nome, setNome] = useState('');
  const [formato, setFormato] = useState<Formato>('mata');
  const [returno, setReturno] = useState(false);
  const [melhorDe, setMelhorDe] = useState<'1' | '3'>('1');
  const [tempo, setTempo] = useState(String(20_000));
  const [sinais, setSinais] = useState<Sinais>('aviso');
  const [data, setData] = useState(() => hojeEmBrasilia(7 * 86_400_000));
  const [hora, setHora] = useState('23:59');
  const [dias, setDias] = useState(7);
  const [aprovacao, setAprovacao] = useState(false);
  const [premio, setPremio] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [criando, setCriando] = useState(false);

  if (!pode(perfil.plano, 'criarLiga')) {
    return (
      <Tela>
        <Topo titulo="Criar campeonato" voltar={voltar} />
        <div className="cadeado-tentos">
          <p className="cadeado">Criar campeonato é do plano Ouro. Seu plano é o {NOME_DO_PLANO[perfil.plano]}: o capitão do seu trio inscreve vocês pelo link do campeonato.</p>
          <button className="btn2 dourado" onClick={() => ir({ tela: 'planos', plano: 'ouro', ciclo: 'mensal' })}>Ver planos</button>
        </div>
      </Tela>
    );
  }

  async function criar() {
    setErro(null);
    const n = nome.trim();
    if (!n) { setErro('Escreva o nome do campeonato.'); return; }
    if (textoOfensivo(n)) { setErro('Escolha outro nome para o campeonato.'); return; }
    if (textoOfensivo(premio)) { setErro('Escolha outro texto para o prêmio.'); return; }
    const ate = instanteDe(data, hora);
    if (!ate || ate <= Date.now()) { setErro('O prazo da inscrição fica no futuro.'); return; }
    setCriando(true);
    try {
      const f = await criarCampeonato(sessao, {
        nome: n, formato, returno: formato === 'pontos' && returno, melhorDe: formato !== 'pontos' && melhorDe === '3' ? 3 : 1,
        regras: { tempoPorJogadaMs: Number(tempo), sinais }, premio: premio.trim(), inscricaoAte: ate, aprovacao, diasPorRodada: dias, comunidade,
      });
      ir({ tela: 'campeonato', slug: f.slug, aba: 'organizar' }, true);
    } catch (e) {
      setErro((e as Error).message);
      setCriando(false);
    }
  }

  return (
    <Tela>
      <div className="campeonatos criar-campeonato">
        <Topo titulo="Criar campeonato" voltar={voltar} />
        <div className="cartao criar-trio">
          <div className="criar-trio-topo">
            <Trofeu tamanho={64} />
            <div className="campo">
              <label htmlFor="nome-campeonato">Nome do campeonato</label>
              <input id="nome-campeonato" value={nome} maxLength={NOME_DO_CAMPEONATO_MAX} autoComplete="off" placeholder="Copa da Firma" disabled={criando}
                onChange={(e) => { setNome(e.target.value); setErro(null); }} />
            </div>
          </div>
          {comunidade && <p className="nota">Campeonato da comunidade: aparece na aba Campeonatos dela.</p>}
        </div>
        <section aria-labelledby="h-formato" className="campo">
          <h2 id="h-formato" className="rotulo">Formato</h2>
          <Opcoes rotulo="Formato" classe="formatos" valor={formato} travado={criando} aoMudar={setFormato}
            opcoes={[['pontos', 'Pontos corridos'], ['mata', 'Mata-mata'], ['grupos', 'Grupos']] as const} />
          <small>
            {formato === 'pontos' ? 'Todos contra todos. Vitória vale 3, derrota 0. Desempate: confronto direto, saldo, pontos feitos e sorteio.'
              : formato === 'mata' ? `De ${TRIOS_MIN} a ${TRIOS_MAX} trios. Vaga que sobra na chave vira passe livre para os primeiros inscritos.`
                : 'Grupos de 4 em pontos corridos; os 2 primeiros de cada grupo vão para o mata-mata. Pede 8, 12, 16... trios.'}
          </small>
          {formato === 'pontos'
            ? (
              <ul className="cartao regras-casa">
                <li><p><b>Turno e returno</b><span>Cada confronto acontece duas vezes.</span></p><Chave rotulo="Turno e returno" ligada={returno} travado={criando} aoMudar={setReturno} /></li>
              </ul>
            )
            : (
              <div className="campo">
                <span className="rotulo-campo">Cada confronto do mata-mata</span>
                <Opcoes rotulo="Cada confronto do mata-mata" valor={melhorDe} travado={criando} aoMudar={setMelhorDe} opcoes={[['1', 'Melhor de 1'], ['3', 'Melhor de 3']] as const} />
              </div>
            )}
        </section>
        <section aria-labelledby="h-inscricao" className="campo">
          <h2 id="h-inscricao" className="rotulo">Inscrição</h2>
          <div className="linha-campos">
            <label className="campo"><span>Inscrições até</span><input type="date" value={data} min={hojeEmBrasilia()} disabled={criando} onChange={(e) => setData(e.target.value)} /></label>
            <label className="campo"><span>Hora</span><input type="time" value={hora} disabled={criando} onChange={(e) => setHora(e.target.value)} /></label>
          </div>
          <ul className="cartao regras-casa">
            <li><p><b>Aprovar cada trio</b><span>Você aprova ou recusa cada inscrição. Sem isso, o trio entra direto.</span></p>
              <Chave rotulo="Aprovar cada trio" ligada={aprovacao} travado={criando} aoMudar={setAprovacao} /></li>
          </ul>
          <small>De {TRIOS_MIN} a {TRIOS_MAX} trios fixos. O capitão inscreve o trio com os 3. Sem ingresso e sem Tentos.</small>
        </section>
        <section aria-labelledby="h-jogos" className="campo">
          <h2 id="h-jogos" className="rotulo">Jogos</h2>
          <label className="campo"><span>Cada rodada dura</span>
            <select value={dias} disabled={criando} onChange={(e) => setDias(Number(e.target.value))}>
              {DIAS.map((d) => <option key={d} value={d}>{d} {d === 1 ? 'dia' : 'dias'}</option>)}
            </select>
          </label>
          <small>Você muda a janela de cada rodada e marca a hora de cada jogo depois. Quem não completar a mesa até {MINUTOS_PARA_WO} minutos depois da hora marcada, ou até o fim da janela, perde por W.O.</small>
        </section>
        <section aria-labelledby="h-regras" className="campo">
          <h2 id="h-regras" className="rotulo">Regras da casa de todos os jogos</h2>
          <span className="rotulo-campo">Tempo por jogada</span>
          <Opcoes rotulo="Tempo por jogada" valor={tempo} travado={criando} aoMudar={setTempo} opcoes={TEMPOS_DE_JOGADA_MS.map((ms) => [String(ms), `${ms / 1000}s`] as const)} />
          <span className="rotulo-campo">Sinais</span>
          <Opcoes rotulo="Sinais" classe="sinais-campeonato" valor={sinais} travado={criando} aoMudar={setSinais}
            opcoes={(['desligados', 'aviso', 'todos'] as const).map((x) => [x, VISIBILIDADE_DOS_SINAIS[x]] as const)} />
          <small>Partida até 12, sem vaga com bot. Quem cai tem 45 s para voltar; depois, o bot joga e o jogo vale.</small>
        </section>
        <div className="campo">
          <label htmlFor="premio-campeonato">Prêmio <small>(opcional)</small></label>
          <textarea id="premio-campeonato" value={premio} maxLength={PREMIO_MAX} rows={2} disabled={criando} placeholder="Um engradado para o trio campeão"
            onChange={(e) => setPremio(e.target.value)} />
          <small className="nota">{AVISO_DO_PREMIO}. Nada de Tentos ou dinheiro pelo jogo.</small>
        </div>
        <p className="nota">Você organiza. O plano Ouro mantém até {CAMPEONATOS_ATIVOS_POR_CONTA} campeonatos ao mesmo tempo.</p>
        {erro && <p className="erro" role="alert">{erro}</p>}
        <button className="btn1 largo" disabled={criando} onClick={() => void criar()}>{criando ? 'CRIANDO...' : 'CRIAR CAMPEONATO'}</button>
      </div>
    </Tela>
  );
}

// ---- a página do campeonato ----
const ABAS: [AbaDoCampeonato, string][] = [['tabela', 'Tabela'], ['jogos', 'Jogos'], ['trios', 'Trios'], ['organizar', 'Organizar']];

export function CampeonatoTela({ sessao, perfil, slug, aba = 'tabela' }: { sessao: Sessao | null; perfil: PerfilConta | null; slug: string; aba?: AbaDoCampeonato }) {
  const [f, setF] = useState<FichaCampeonato | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [versao, setVersao] = useState(0);
  useEffect(() => {
    let vivo = true;
    lerCampeonato(sessao, slug).then((x) => { if (vivo) setF(x); }, (e: Error) => { if (vivo) setErro(e.message); });
    return () => { vivo = false; };
  }, [sessao?.id, slug, versao]);
  // Só a página que o organizador deixou pública entra nos buscadores.
  useEffect(() => {
    if (!f || f.publico) return;
    const m = document.createElement('meta');
    m.name = 'robots'; m.content = 'noindex';
    document.head.appendChild(m);
    return () => { m.remove(); };
  }, [f?.publico]);
  // GA4: o fim do campeonato, uma vez por aparelho, para quem organizou ou jogou. Sem o nome nem o endereço.
  useEffect(() => {
    if (f?.estado === 'concluido' && (f.eu?.organizador || f.eu?.trio)) medirUmaVez('campeonato:' + f.id, 'campeonato_concluido', { formato: f.formato });
  }, [f?.estado]);
  if (erro) return <Tela><Topo titulo="Campeonato" voltar={voltar} marca /><p className="erro" role="alert">{erro}</p></Tela>;
  if (!f) return <Carregando />;
  const org = !!f.eu?.organizador;
  const abas = ABAS.filter(([a]) => a !== 'organizar' || org);
  const atual = abas.some(([a]) => a === aba) ? aba : 'tabela';
  const mudou = (x: FichaCampeonato) => setF(x);
  return (
    <Tela larga>
      <div className="campeonato">
        <Topo titulo="Campeonato" voltar={voltar} marca />
        <Cabecalho f={f} />
        {f.estado === 'cancelado' && <p className="cartao aviso-leitura" role="status">Campeonato cancelado. Motivo: {f.motivoCancelamento}</p>}
        <SuaParte sessao={sessao} perfil={perfil} f={f} aoMudar={mudou} />
        <div className="abas-comunidade" role="tablist" aria-label="Campeonato">
          {abas.map(([a, nome]) => (
            <button key={a} role="tab" aria-selected={atual === a} className={atual === a ? 'on' : undefined}
              onClick={() => { if (atual !== a) ir({ tela: 'campeonato', slug, ...(a === 'tabela' ? {} : { aba: a }) }, true); }}>{nome}</button>
          ))}
        </div>
        <div className="aba-comunidade" role="tabpanel">
          {atual === 'tabela' && <Tabela f={f} sessao={sessao} perfil={perfil} aoMudar={mudou} />}
          {atual === 'jogos' && <Jogos f={f} sessao={sessao} perfil={perfil} aoMudar={mudou} />}
          {atual === 'trios' && <Trios f={f} sessao={sessao} aoMudar={mudou} />}
          {atual === 'organizar' && sessao && <Organizar f={f} sessao={sessao} aoMudar={mudou} aoRecarregar={() => setVersao((v) => v + 1)} />}
        </div>
      </div>
    </Tela>
  );
}

function Cabecalho({ f }: { f: FichaCampeonato }) {
  const aprovados = f.trios.filter((t) => t.estado === 'aprovada').length;
  const detalhes = [f.nomeDoFormato + (f.returno ? ', turno e returno' : ''), `${aprovados} ${aprovados === 1 ? 'trio' : 'trios'}`];
  if (f.formato !== 'pontos' && f.melhorDe === 3) detalhes.push('melhor de 3 no mata-mata');
  const campeao = f.campeao ? f.trios.find((t) => t.id === f.campeao) : null;
  return (
    <>
      <div className="cartao trio-cab campeonato-cab">
        <Trofeu tamanho={60} />
        <div>
          <span className={'etiqueta estado-' + f.estado}>{ESTADO[f.estado]}{f.estado === 'inscricao' ? ` até ${diaDoJogo(f.inscricaoAte)}` : ''}</span>
          <h2>{f.nome}</h2>
          <p className="nota">{detalhes.join(' · ')}</p>
          <p className="nota">Organização: <b>{f.organizador.apelido}</b>{f.comunidade && <> · <button className="link" onClick={() => ir({ tela: 'comunidade', id: f.comunidade!.id })}>{f.comunidade.nome}</button></>}</p>
        </div>
      </div>
      {campeao && (
        <div className="cartao campeao" role="status">
          <Escudo id={campeao.escudo} tamanho={52} />
          <p><small>CAMPEÃO</small><b>{campeao.nome}</b></p>
        </div>
      )}
      {f.premio && (
        <div className="cartao premio">
          <p><b>Prêmio:</b> {f.premio}</p>
          <small className="nota">{f.avisoDoPremio}</small>
        </div>
      )}
    </>
  );
}

// Inscrição (com o capitão) e o seu próximo jogo.
function SuaParte({ sessao, perfil, f, aoMudar }: { sessao: Sessao | null; perfil: PerfilConta | null; f: FichaCampeonato; aoMudar: (f: FichaCampeonato) => void }) {
  const [trio, setTrio] = useState<string | null>(f.eu?.podeInscrever[0]?.id ?? null);
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const meu = f.eu?.trio ? f.trios.find((t) => t.id === f.eu!.trio) ?? null : null;
  if (f.estado === 'inscricao') {
    const aberta = Date.now() <= f.inscricaoAte;
    if (!sessao || sessao.anonimo || !perfil) {
      return aberta ? (
        <div className="cartao inscricao">
          <p>Para inscrever o trio, entre com a conta Google. O capitão inscreve o trio fixo com os 3.</p>
          <button className="btn1 largo" onClick={() => { guardarDestino({ tela: 'campeonato', slug: f.slug }); ir({ tela: 'inicio' }); }}>ENTRAR PARA INSCREVER</button>
        </div>
      ) : null;
    }
    if (meu) {
      const capitao = meu.capitao === sessao.id;
      return (
        <div className="cartao inscricao">
          <p><b>{meu.nome}</b> {meu.estado === 'pendente' ? 'está inscrito e espera o organizador aprovar.' : 'está inscrito.'}</p>
          {capitao && <button className="btn2" disabled={ocupado} onClick={() => { setOcupado(true); decidirInscricao(sessao, f.id, meu.id, 'tirar').then(aoMudar, (e: Error) => avisarNaTela(e.message)).finally(() => setOcupado(false)); }}>Tirar a inscrição</button>}
        </div>
      );
    }
    if (!aberta) return <p className="cartao nota">O prazo da inscrição acabou. O campeonato começa quando o organizador fechar a tabela.</p>;
    const opcoes = f.eu?.podeInscrever ?? [];
    if (!opcoes.length && f.eu?.organizador) return null;
    if (!opcoes.length) {
      return (
        <div className="cartao inscricao">
          <p>Para inscrever, você precisa ser capitão de um trio fixo completo, com ninguém já inscrito noutro trio deste campeonato.</p>
          <button className="btn2" onClick={() => ir({ tela: 'trios' })}>Ver trios fixos</button>
        </div>
      );
    }
    async function inscrever() {
      if (!trio || !sessao) return;
      setErro(null); setOcupado(true);
      try {
        const x = await inscreverTrio(sessao, f.id, trio);
        medir('campeonato_inscricao', { formato: f.formato });
        avisarNaTela(f.aprovacao ? 'Inscrição enviada. O organizador aprova.' : 'Trio inscrito!');
        aoMudar(x);
      } catch (e) { setErro((e as Error).message); } finally { setOcupado(false); }
    }
    return (
      <div className="cartao inscricao">
        <p><b>Inscreva o seu trio</b></p>
        <div className="escolher-trio" role="radiogroup" aria-label="Trio para inscrever">
          {opcoes.map((t) => (
            <button key={t.id} role="radio" aria-checked={trio === t.id} className={'cartao' + (trio === t.id ? ' on' : '')} onClick={() => setTrio(t.id)}>
              <Escudo id={t.escudo} tamanho={34} /><b>{t.nome}</b><span className="marca-escolha" aria-hidden="true">{trio === t.id ? '✓' : ''}</span>
            </button>
          ))}
        </div>
        {erro && <p className="erro" role="alert">{erro}</p>}
        <button className="btn1 largo" disabled={ocupado || !trio} onClick={() => void inscrever()}>INSCREVER TRIO</button>
        {f.aprovacao && <small className="nota">O organizador aprova cada trio.</small>}
      </div>
    );
  }
  if (f.estado !== 'andamento' || !meu || !sessao || !perfil) return null;
  const proximo = f.jogos.filter((j) => j.meu && !j.vencedor && !j.wo && j.a && j.b).sort((x, y) => (x.marcadoPara ?? x.fim) - (y.marcadoPara ?? y.fim))[0];
  if (!proximo) return <p className="cartao nota">{meu.nome}: nenhum jogo em aberto agora. A tabela anda sozinha depois de cada resultado.</p>;
  return (
    <section aria-labelledby="h-seu-jogo" className="seu-jogo">
      <h2 id="h-seu-jogo" className="rotulo">Seu próximo jogo</h2>
      <CartaoJogo f={f} j={proximo} sessao={sessao} perfil={perfil} aoMudar={aoMudar} destaque />
    </section>
  );
}

// ---- tabela e chave ----
function Tabela({ f, sessao, perfil, aoMudar }: { f: FichaCampeonato; sessao: Sessao | null; perfil: PerfilConta | null; aoMudar: (f: FichaCampeonato) => void }) {
  if (f.estado === 'inscricao') {
    return <p className="cartao vazio-lista">A tabela sai quando o organizador começar o campeonato, com os trios aprovados na ordem da inscrição.</p>;
  }
  const mata = f.jogos.filter((j) => j.fase === 'mata');
  return (
    <>
      {f.classificacao.map((t) => <Classificacao key={t.grupo ?? 'geral'} f={f} titulo={t.grupo === null ? 'Classificação' : grupoDe(t.grupo)} linhas={t.linhas} />)}
      {mata.length > 0 && <ChaveDoMata f={f} jogos={mata} sessao={sessao} perfil={perfil} aoMudar={aoMudar} />}
    </>
  );
}

function Classificacao({ f, titulo, linhas }: { f: FichaCampeonato; titulo: string; linhas: FichaCampeonato['classificacao'][number]['linhas'] }) {
  return (
    <section aria-label={titulo}>
      <h2 className="rotulo">{titulo}</h2>
      <div className="cartao tabela-campeonato">
        <table>
          <thead><tr><th scope="col" aria-label="Posição">#</th><th scope="col">Trio</th><th scope="col" aria-label="Pontos">P</th><th scope="col" aria-label="Jogos">J</th><th scope="col" aria-label="Vitórias">V</th><th scope="col" aria-label="Saldo">S</th></tr></thead>
          <tbody>
            {linhas.map((l) => {
              const t = f.trios.find((x) => x.id === l.trio);
              return (
                <tr key={l.trio} className={l.trio === f.eu?.trio ? 'meu' : undefined}>
                  <td>{l.posicao}</td>
                  <td><span className="trio-na-tabela">{t && <Escudo id={t.escudo} tamanho={22} />}<span>{t?.nome ?? 'Trio'}</span></span></td>
                  <td><b>{l.pontos}</b></td><td>{l.jogos}</td><td>{l.vitorias}</td><td>{l.saldo > 0 ? `+${l.saldo}` : l.saldo}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {f.formato === 'grupos' && <small className="nota">Os 2 primeiros de cada grupo vão para o mata-mata.</small>}
    </section>
  );
}

function ChaveDoMata({ f, jogos, sessao, perfil, aoMudar }: { f: FichaCampeonato; jogos: JogoNaTela[]; sessao: Sessao | null; perfil: PerfilConta | null; aoMudar: (f: FichaCampeonato) => void }) {
  const rodadas = [...new Set(jogos.map((j) => j.rodada))].sort((a, b) => a - b);
  return (
    <section aria-label="Chave do mata-mata" className="chave-mata">
      <h2 className="rotulo">Chave</h2>
      {rodadas.map((r) => {
        const daRodada = jogos.filter((j) => j.rodada === r).sort((a, b) => a.posicao - b.posicao);
        return (
          <div key={r} className="rodada-chave">
            <h3>{daRodada[0]!.nomeDaRodada}</h3>
            <ul>{daRodada.map((j) => <li key={j.id}><CartaoJogo f={f} j={j} sessao={sessao} perfil={perfil} aoMudar={aoMudar} compacto /></li>)}</ul>
          </div>
        );
      })}
    </section>
  );
}

// ---- jogos ----
function Jogos({ f, sessao, perfil, aoMudar }: { f: FichaCampeonato; sessao: Sessao | null; perfil: PerfilConta | null; aoMudar: (f: FichaCampeonato) => void }) {
  if (f.estado === 'inscricao') return <p className="cartao vazio-lista">Os jogos aparecem quando o campeonato começar.</p>;
  const decidido = (j: JogoNaTela) => j.vencedor !== null || j.wo !== null || (j.a === '' && j.b === '');
  const proximos = f.jogos.filter((j) => !decidido(j) && j.a && j.b).sort((x, y) => (x.marcadoPara ?? x.fim) - (y.marcadoPara ?? y.fim));
  const resultados = f.jogos.filter((j) => decidido(j) && j.a && j.b).sort((x, y) => y.rodada - x.rodada || x.posicao - y.posicao);
  return (
    <>
      <section aria-labelledby="h-proximos">
        <h2 id="h-proximos" className="rotulo">Próximos jogos{proximos.length ? ` · ${proximos.length}` : ''}</h2>
        {proximos.length === 0 ? <p className="nota">Nenhum jogo em aberto agora.</p>
          : <ul className="jogos-campeonato">{proximos.map((j) => <li key={j.id}><CartaoJogo f={f} j={j} sessao={sessao} perfil={perfil} aoMudar={aoMudar} /></li>)}</ul>}
      </section>
      <section aria-labelledby="h-resultados">
        <h2 id="h-resultados" className="rotulo">Resultados{resultados.length ? ` · ${resultados.length}` : ''}</h2>
        {resultados.length === 0 ? <p className="nota">Nenhum resultado ainda.</p>
          : <ul className="jogos-campeonato">{resultados.map((j) => <li key={j.id}><CartaoJogo f={f} j={j} sessao={sessao} perfil={perfil} aoMudar={aoMudar} /></li>)}</ul>}
      </section>
    </>
  );
}

// O placar do confronto: os pontos da partida no melhor de 1, as partidas ganhas no melhor de 3, 12 x 0 no W.O.
function placarDoJogo(j: JogoNaTela): [string, string] | null {
  if (j.wo === 'ambos') return ['W.O.', 'W.O.'];
  if (j.wo) return j.wo === 'a' ? ['0', '12'] : ['12', '0'];
  if (!j.partidas.length) return null;
  if (j.melhorDe === 1 && j.partidas.length === 1) return [String(j.partidas[0]!.pontos[0]), String(j.partidas[0]!.pontos[1])];
  return [String(j.partidas.filter((p) => p.vencedor === 'a').length), String(j.partidas.filter((p) => p.vencedor === 'b').length)];
}

function CartaoJogo({ f, j, sessao, perfil, aoMudar, compacto = false, destaque = false }: {
  f: FichaCampeonato; j: JogoNaTela; sessao: Sessao | null; perfil: PerfilConta | null; aoMudar: (f: FichaCampeonato) => void; compacto?: boolean; destaque?: boolean;
}) {
  const [folha, setFolha] = useState<'marcar' | 'resultado' | null>(null);
  const [entrando, setEntrando] = useState(false);
  const agora = Date.now(), org = !!f.eu?.organizador && f.estado === 'andamento';
  const fechado = j.vencedor !== null || j.wo !== null;
  const definidos = !!j.a && !!j.b && j.a !== '' && j.b !== '';
  const placar = placarDoJogo(j);
  const aberto = definidos && !fechado && f.estado === 'andamento' && agora >= j.inicio && (agora < j.prazo || j.partidas.length > 0);
  const status = fechado ? (j.wo === 'ambos' ? 'W.O. dos dois' : j.wo ? 'W.O.' : j.manual ? 'Resultado do organizador' : j.melhorDe === 3 ? `Melhor de 3 · ${j.partidas.length} partidas` : 'Encerrado')
    : !definidos ? 'A definir'
      : j.marcadoPara ? `Marcado: ${quandoDoJogo(j.marcadoPara)}`
        : agora < j.inicio ? `Abre em ${diaDoJogo(j.inicio)}` : `Até ${diaDoJogo(j.fim)}`;
  const lado = (t: string | null, l: 'a' | 'b', p: string | undefined) => {
    const trio = t ? f.trios.find((x) => x.id === t) : null, venceu = j.vencedor === l;
    return (
      <div className={'lado-jogo' + (venceu ? ' venceu' : '') + (t === f.eu?.trio && t ? ' meu' : '')}>
        {trio ? <Escudo id={trio.escudo} tamanho={compacto ? 22 : 28} /> : <span className="escudo-vazio" aria-hidden="true" />}
        <span className="nome-lado">{nomeDoLado(f, t)}</span>
        {p !== undefined && <b className="pontos-lado">{p}</b>}
      </div>
    );
  };
  async function entrar() {
    if (!sessao || !perfil) return;
    if (semPartidasAgora()) { ir({ tela: 'limite' }); return; } // sem partidas grátis hoje: a página do limite (item 69)
    setEntrando(true);
    try { ir({ tela: 'sala', codigo: await salaDoCampeonato(sessao, perfil, j.id) }); } catch (e) { avisarNaTela((e as Error).message); setEntrando(false); }
  }
  return (
    <div className={'cartao jogo-campeonato' + (compacto ? ' compacto' : '') + (destaque ? ' destaque' : '') + (j.meu ? ' meu' : '')}>
      {!compacto && <p className="topo-jogo"><span>{j.nomeDaRodada}{j.grupo !== null && j.fase === 'grupo' ? ` · ${grupoDe(j.grupo)}` : ''}</span><span className="status-jogo">{status}</span></p>}
      {lado(j.a, 'a', placar?.[0])}
      {lado(j.b, 'b', placar?.[1])}
      {compacto && definidos && <small className="status-jogo">{status}</small>}
      {j.motivo && !compacto && <p className="motivo-jogo">Motivo do organizador: {j.motivo}</p>}
      {!compacto && definidos && !fechado && (j.meu || destaque) && (
        <small className="nota">Quem não completar a mesa até {j.marcadoPara ? `${MINUTOS_PARA_WO} minutos depois da hora marcada` : `o fim da janela (${quandoDoJogo(j.fim)})`} perde por W.O.</small>
      )}
      {!compacto && (
        <div className="acoes-jogo">
          {j.meu && aberto && sessao && perfil && <button className="btn1" disabled={entrando} onClick={() => void entrar()}>{entrando ? 'ENTRANDO...' : 'ENTRAR NO JOGO'}</button>}
          {!j.meu && j.transmitido && j.sala && !fechado && <button className="btn2" onClick={() => ir({ tela: 'assistir', codigo: j.sala, amigo: null })}>Assistir</button>}
          {org && definidos && (!fechado || !j.partidas.length) && (
            <button className="btn2" onClick={() => setFolha('marcar')}>{j.marcadoPara || fechado ? 'Remarcar' : 'Marcar hora'}</button>
          )}
          {org && definidos && (!fechado || !j.partidas.length) && <button className="btn2" onClick={() => setFolha('resultado')}>Lançar resultado</button>}
        </div>
      )}
      {folha === 'marcar' && sessao && <MarcarJogo f={f} j={j} sessao={sessao} aoFechar={(x) => { setFolha(null); if (x) aoMudar(x); }} />}
      {folha === 'resultado' && sessao && <ResultadoAMao f={f} j={j} sessao={sessao} aoFechar={(x) => { setFolha(null); if (x) aoMudar(x); }} />}
    </div>
  );
}

function CampoMotivo({ valor, aoMudar, obrigatorio }: { valor: string; aoMudar: (v: string) => void; obrigatorio: boolean }) {
  return (
    <label className="campo">
      <span>Motivo {obrigatorio ? '' : <small>(opcional)</small>}</span>
      <textarea value={valor} maxLength={MOTIVO_MAX} rows={2} placeholder="Os dois trios veem o motivo." onChange={(e) => aoMudar(e.target.value)} />
    </label>
  );
}

function MarcarJogo({ f, j, sessao, aoFechar }: { f: FichaCampeonato; j: JogoNaTela; sessao: Sessao; aoFechar: (f: FichaCampeonato | null) => void }) {
  const remarcar = !!j.marcadoPara || j.vencedor !== null || j.wo !== null;
  const base = j.marcadoPara ?? Math.max(Date.now() + 3_600_000, j.inicio);
  const [data, setData] = useState(() => new Date(base - 3 * 3_600_000).toISOString().slice(0, 10));
  const [hora, setHora] = useState(() => (j.marcadoPara ? new Date(j.marcadoPara - 3 * 3_600_000).toISOString().slice(11, 16) : '20:00'));
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  async function salvar(semHora: boolean) {
    setErro(null);
    const quando = semHora ? null : instanteDe(data, hora);
    if (!semHora && (!quando || quando <= Date.now())) { setErro('Escolha uma hora no futuro.'); return; }
    if (remarcar && !motivo.trim()) { setErro('Escreva o motivo: os dois trios veem.'); return; }
    if (textoOfensivo(motivo)) { setErro('Escolha outro texto para o motivo.'); return; }
    setOcupado(true);
    try { const x = await marcarJogo(sessao, f.id, j.id, quando, motivo.trim()); avisarNaTela('Os dois trios receberam o aviso.'); aoFechar(x); } catch (e) { setErro((e as Error).message); setOcupado(false); }
  }
  return (
    <Folha titulo={remarcar ? 'Remarcar o jogo' : 'Marcar a hora do jogo'} aoFechar={() => aoFechar(null)}>
      <p>{nomeDoLado(f, j.a)} x {nomeDoLado(f, j.b)}</p>
      {(j.vencedor !== null || j.wo !== null) && <p className="nota">O resultado sai e o jogo volta a ficar em aberto, com sala nova.</p>}
      <div className="linha-campos">
        <label className="campo"><span>Data</span><input type="date" value={data} min={hojeEmBrasilia()} disabled={ocupado} onChange={(e) => setData(e.target.value)} /></label>
        <label className="campo"><span>Hora</span><input type="time" value={hora} disabled={ocupado} onChange={(e) => setHora(e.target.value)} /></label>
      </div>
      <CampoMotivo valor={motivo} aoMudar={setMotivo} obrigatorio={remarcar} />
      <small className="nota">W.O. {MINUTOS_PARA_WO} minutos depois da hora marcada para quem não completar a mesa.</small>
      {erro && <p className="erro" role="alert">{erro}</p>}
      <button className="btn1 largo" disabled={ocupado} onClick={() => void salvar(false)}>{remarcar ? 'REMARCAR' : 'MARCAR'}</button>
      {j.marcadoPara && <button className="btn2 largo" disabled={ocupado} onClick={() => void salvar(true)}>Tirar a hora marcada</button>}
      <button className="btn2 largo" onClick={() => aoFechar(null)}>Agora não</button>
    </Folha>
  );
}

function ResultadoAMao({ f, j, sessao, aoFechar }: { f: FichaCampeonato; j: JogoNaTela; sessao: Sessao; aoFechar: (f: FichaCampeonato | null) => void }) {
  const a = nomeDoLado(f, j.a), b = nomeDoLado(f, j.b);
  const [escolha, setEscolha] = useState<'va' | 'vb' | 'woa' | 'wob' | 'wo2'>('va');
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  async function lancar() {
    setErro(null);
    if (!motivo.trim()) { setErro('Escreva o motivo: os dois trios veem.'); return; }
    if (textoOfensivo(motivo)) { setErro('Escolha outro texto para o motivo.'); return; }
    const b = escolha === 'va' ? { vencedor: 'a' as const } : escolha === 'vb' ? { vencedor: 'b' as const } : { wo: escolha === 'woa' ? 'a' as const : escolha === 'wob' ? 'b' as const : 'ambos' as const };
    setOcupado(true);
    try { const x = await lancarResultado(sessao, f.id, j.id, { ...b, motivo: motivo.trim() }); avisarNaTela('Resultado lançado. A tabela já andou.'); aoFechar(x); } catch (e) { setErro((e as Error).message); setOcupado(false); }
  }
  const opcoes = [['va', `Vitória de ${a}`], ['vb', `Vitória de ${b}`], ['woa', `W.O. de ${a}`], ['wob', `W.O. de ${b}`], ['wo2', 'W.O. dos dois trios']] as const;
  return (
    <Folha titulo="Lançar o resultado" aoFechar={() => aoFechar(null)}>
      <div className="escolher-trio" role="radiogroup" aria-label="Resultado">
        {opcoes.map(([v, nome]) => (
          <button key={v} role="radio" aria-checked={escolha === v} className={'cartao' + (escolha === v ? ' on' : '')} onClick={() => setEscolha(v)}>
            <b>{nome}</b><span className="marca-escolha" aria-hidden="true">{escolha === v ? '✓' : ''}</span>
          </button>
        ))}
      </div>
      <CampoMotivo valor={motivo} aoMudar={setMotivo} obrigatorio />
      {erro && <p className="erro" role="alert">{erro}</p>}
      <button className="btn1 largo" disabled={ocupado} onClick={() => void lancar()}>LANÇAR RESULTADO</button>
      <button className="btn2 largo" onClick={() => aoFechar(null)}>Agora não</button>
    </Folha>
  );
}

// ---- trios ----
const ESTADO_DA_INSCRICAO: Record<TrioNaTela['estado'], string> = { pendente: 'Esperando aprovação', aprovada: 'Inscrito', recusada: 'Recusado' };

function Trios({ f, sessao, aoMudar }: { f: FichaCampeonato; sessao: Sessao | null; aoMudar: (f: FichaCampeonato) => void }) {
  const org = !!f.eu?.organizador && f.estado === 'inscricao';
  const [ocupado, setOcupado] = useState(false);
  const fazer = (trio: string, acao: 'aprovar' | 'recusar' | 'tirar') => {
    if (!sessao) return;
    setOcupado(true);
    decidirInscricao(sessao, f.id, trio, acao).then(aoMudar, (e: Error) => avisarNaTela(e.message)).finally(() => setOcupado(false));
  };
  const ordem: Record<TrioNaTela['estado'], number> = { pendente: 0, aprovada: 1, recusada: 2 };
  const lista = [...f.trios].sort((a, b) => ordem[a.estado] - ordem[b.estado]);
  if (!lista.length) return <p className="cartao vazio-lista">Nenhum trio inscrito ainda.</p>;
  return (
    <ul className="trios-campeonato">
      {lista.map((t) => (
        <li key={t.id} className={'cartao' + (t.id === f.eu?.trio ? ' meu' : '')}>
          <div className="cab-trio-campeonato">
            <Escudo id={t.escudo} tamanho={40} />
            <span className="quem"><b>{t.nome}</b><small className={'estado-inscricao ' + t.estado}>{ESTADO_DA_INSCRICAO[t.estado]}</small></span>
          </div>
          <ul className="membros-trio-campeonato">
            {t.membros.map((m) => (
              <li key={m.usuario}><Rosto perfil={{ apelido: m.apelido, avatar: m.avatar }} usuario={m.usuario} cor="var(--ink-300)" tamanho={30} /><span>{m.apelido}{m.usuario === t.capitao ? ' · capitão' : ''}</span></li>
            ))}
          </ul>
          {org && (
            <div className="acoes-jogo">
              {t.estado !== 'aprovada' && <button className="btn2" disabled={ocupado} onClick={() => fazer(t.id, 'aprovar')}>Aprovar</button>}
              {t.estado === 'pendente' && <button className="btn2" disabled={ocupado} onClick={() => fazer(t.id, 'recusar')}>Recusar</button>}
              {t.estado === 'aprovada' && <button className="btn2" disabled={ocupado} onClick={() => fazer(t.id, 'tirar')}>Tirar do campeonato</button>}
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}

// ---- organizar ----
function Organizar({ f, sessao, aoMudar, aoRecarregar }: { f: FichaCampeonato; sessao: Sessao; aoMudar: (f: FichaCampeonato) => void; aoRecarregar: () => void }) {
  const [folha, setFolha] = useState<'comecar' | 'cancelar' | 'premio' | number | null>(null);
  const aprovados = f.trios.filter((t) => t.estado === 'aprovada').length, pendentes = f.trios.filter((t) => t.estado === 'pendente').length;
  const falta = conferirQuantidade(f.formato, aprovados);
  const rodadas = [...new Set(f.jogos.filter((j) => j.vencedor === null && j.wo === null && !(j.a === '' && j.b === '')).map((j) => j.rodada))].sort((a, b) => a - b);
  return (
    <>
      {f.estado === 'inscricao' && (
        <section aria-labelledby="h-comecar" className="cartao organizar">
          <h2 id="h-comecar" className="rotulo">Começar</h2>
          <p><b>{aprovados}</b> {aprovados === 1 ? 'trio aprovado' : 'trios aprovados'}{pendentes ? ` e ${pendentes} esperando aprovação (aprove na aba Trios)` : ''}.</p>
          <small className="nota">{falta ?? 'Ao começar, as inscrições fecham, a tabela sai com os trios aprovados na ordem da inscrição e os trios da primeira rodada recebem o aviso.'}</small>
          <button className="btn1 largo" disabled={!!falta} onClick={() => setFolha('comecar')}>COMEÇAR O CAMPEONATO</button>
        </section>
      )}
      {f.estado === 'andamento' && rodadas.length > 0 && (
        <section aria-labelledby="h-janelas">
          <h2 id="h-janelas" className="rotulo">Janelas das rodadas</h2>
          <ul className="janelas-campeonato">
            {rodadas.map((r) => {
              const j = f.jogos.find((x) => x.rodada === r && x.vencedor === null && x.wo === null)!;
              return (
                <li key={r} className="cartao">
                  <span className="quem"><b>{j.nomeDaRodada}</b><small>{diaDoJogo(j.inicio)} a {quandoDoJogo(j.fim)}</small></span>
                  <button className="btn2" onClick={() => setFolha(r)}>Mudar</button>
                </li>
              );
            })}
          </ul>
          <small className="nota">A hora de cada jogo, o remarcar e o resultado à mão ficam nos jogos, na aba Jogos.</small>
        </section>
      )}
      {f.estado !== 'cancelado' && <Ferramentas f={f} sessao={sessao} aoMudar={aoMudar} />}
      {(f.estado === 'inscricao' || f.estado === 'andamento') && (
        <div className="rodape-comunidade">
          <button className="btn2" onClick={() => setFolha('premio')}>{f.premio ? 'Mudar o prêmio' : 'Escrever o prêmio'}</button>
          {f.estado === 'inscricao' && <button className="link" onClick={() => setFolha('cancelar')}>Cancelar o campeonato</button>}
        </div>
      )}
      {folha === 'comecar' && <Comecar f={f} sessao={sessao} aoFechar={(x) => { setFolha(null); if (x) aoMudar(x); }} />}
      {typeof folha === 'number' && <JanelaDaRodada f={f} sessao={sessao} rodada={folha} aoFechar={(x) => { setFolha(null); if (x) aoMudar(x); }} />}
      {folha === 'premio' && <EditarPremio f={f} sessao={sessao} aoFechar={(x) => { setFolha(null); if (x) aoMudar(x); }} />}
      {folha === 'cancelar' && <Cancelar f={f} sessao={sessao} aoFechar={(ok) => { setFolha(null); if (ok) aoRecarregar(); }} />}
    </>
  );
}

function Comecar({ f, sessao, aoFechar }: { f: FichaCampeonato; sessao: Sessao; aoFechar: (f: FichaCampeonato | null) => void }) {
  const [quando, setQuando] = useState<'agora' | 'depois'>('agora');
  const [data, setData] = useState(() => hojeEmBrasilia(86_400_000));
  const [hora, setHora] = useState('00:00');
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  async function comecar() {
    setErro(null);
    const inicio = quando === 'agora' ? null : instanteDe(data, hora);
    if (quando === 'depois' && !inicio) { setErro('Escolha a data e a hora.'); return; }
    setOcupado(true);
    try { aoFechar(await comecarCampeonato(sessao, f.id, inicio)); } catch (e) { setErro((e as Error).message); setOcupado(false); }
  }
  return (
    <Folha titulo="Começar o campeonato?" aoFechar={() => aoFechar(null)}>
      <p>As inscrições fecham e a tabela sai agora. Cada rodada dura {f.diasPorRodada} {f.diasPorRodada === 1 ? 'dia' : 'dias'}.</p>
      <Opcoes rotulo="Primeira rodada" valor={quando} aoMudar={setQuando} opcoes={[['agora', 'Abre agora'], ['depois', 'Abre depois']] as const} />
      {quando === 'depois' && (
        <div className="linha-campos">
          <label className="campo"><span>Data</span><input type="date" value={data} min={hojeEmBrasilia()} onChange={(e) => setData(e.target.value)} /></label>
          <label className="campo"><span>Hora</span><input type="time" value={hora} onChange={(e) => setHora(e.target.value)} /></label>
        </div>
      )}
      {erro && <p className="erro" role="alert">{erro}</p>}
      <button className="btn1 largo" disabled={ocupado} onClick={() => void comecar()}>COMEÇAR</button>
      <button className="btn2 largo" onClick={() => aoFechar(null)}>Agora não</button>
    </Folha>
  );
}

function JanelaDaRodada({ f, sessao, rodada, aoFechar }: { f: FichaCampeonato; sessao: Sessao; rodada: number; aoFechar: (f: FichaCampeonato | null) => void }) {
  const j = f.jogos.find((x) => x.rodada === rodada && x.vencedor === null && x.wo === null)!;
  const dia = (ms: number) => new Date(ms - 3 * 3_600_000).toISOString().slice(0, 10), hr = (ms: number) => new Date(ms - 3 * 3_600_000).toISOString().slice(11, 16);
  const [di, setDi] = useState(dia(j.inicio)), [hi, setHi] = useState(hr(j.inicio));
  const [df, setDf] = useState(dia(j.fim)), [hf, setHf] = useState(hr(j.fim));
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  async function salvar() {
    setErro(null);
    const inicio = instanteDe(di, hi), fim = instanteDe(df, hf);
    if (!inicio || !fim || fim <= inicio || fim <= Date.now()) { setErro('A janela termina depois de começar, e no futuro.'); return; }
    setOcupado(true);
    try { aoFechar(await mudarJanela(sessao, f.id, rodada, inicio, fim)); } catch (e) { setErro((e as Error).message); setOcupado(false); }
  }
  return (
    <Folha titulo={`Janela: ${j.nomeDaRodada}`} aoFechar={() => aoFechar(null)}>
      <div className="linha-campos">
        <label className="campo"><span>Começa</span><input type="date" value={di} onChange={(e) => setDi(e.target.value)} /></label>
        <label className="campo"><span>Hora</span><input type="time" value={hi} onChange={(e) => setHi(e.target.value)} /></label>
      </div>
      <div className="linha-campos">
        <label className="campo"><span>Termina</span><input type="date" value={df} onChange={(e) => setDf(e.target.value)} /></label>
        <label className="campo"><span>Hora</span><input type="time" value={hf} onChange={(e) => setHf(e.target.value)} /></label>
      </div>
      <small className="nota">Vale para os jogos da rodada que ainda não foram decididos. Sem hora marcada, o W.O. sai no fim da janela.</small>
      {erro && <p className="erro" role="alert">{erro}</p>}
      <button className="btn1 largo" disabled={ocupado} onClick={() => void salvar()}>SALVAR JANELA</button>
      <button className="btn2 largo" onClick={() => aoFechar(null)}>Agora não</button>
    </Folha>
  );
}

function EditarPremio({ f, sessao, aoFechar }: { f: FichaCampeonato; sessao: Sessao; aoFechar: (f: FichaCampeonato | null) => void }) {
  const [premio, setPremio] = useState(f.premio ?? '');
  const [erro, setErro] = useState<string | null>(null);
  async function salvar() {
    if (textoOfensivo(premio)) { setErro('Escolha outro texto para o prêmio.'); return; }
    try { aoFechar(await editarCampeonato(sessao, f.id, { premio: premio.trim() })); } catch (e) { setErro((e as Error).message); }
  }
  return (
    <Folha titulo="Prêmio" aoFechar={() => aoFechar(null)}>
      <label className="campo"><span>Prêmio <small>(opcional)</small></span>
        <textarea value={premio} maxLength={PREMIO_MAX} rows={2} placeholder="Um engradado para o trio campeão" onChange={(e) => setPremio(e.target.value)} />
      </label>
      <small className="nota">{AVISO_DO_PREMIO}.</small>
      {erro && <p className="erro" role="alert">{erro}</p>}
      <button className="btn1 largo" onClick={() => void salvar()}>SALVAR</button>
      <button className="btn2 largo" onClick={() => aoFechar(null)}>Agora não</button>
    </Folha>
  );
}

function Cancelar({ f, sessao, aoFechar }: { f: FichaCampeonato; sessao: Sessao; aoFechar: (ok: boolean) => void }) {
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  async function cancelar() {
    if (!motivo.trim()) { setErro('Escreva o motivo: os trios inscritos recebem.'); return; }
    try { await cancelarCampeonato(sessao, f.id, motivo.trim()); avisarNaTela('Campeonato cancelado.'); aoFechar(true); } catch (e) { setErro((e as Error).message); }
  }
  return (
    <Folha titulo={`Cancelar ${f.nome}?`} aoFechar={() => aoFechar(false)}>
      <p>Os trios inscritos recebem o aviso com o motivo. Depois de começar, o campeonato vai até o fim.</p>
      <CampoMotivo valor={motivo} aoMudar={setMotivo} obrigatorio />
      {erro && <p className="erro" role="alert">{erro}</p>}
      <button className="btn2 perigo largo" onClick={() => void cancelar()}>CANCELAR O CAMPEONATO</button>
      <button className="btn2 largo" onClick={() => aoFechar(false)}>Agora não</button>
    </Folha>
  );
}

// ---- ferramentas de organizador (item 8) ----
function Ferramentas({ f, sessao, aoMudar }: { f: FichaCampeonato; sessao: Sessao; aoMudar: (f: FichaCampeonato) => void }) {
  const [folha, setFolha] = useState<'amigos' | 'aviso' | null>(null);
  const [copiado, setCopiado] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const link = linkDoCampeonato(f.slug), ativo = f.estado === 'inscricao' || f.estado === 'andamento';
  const zap = 'https://wa.me/?text=' + encodeURIComponent(`Bora jogar o ${f.nome} no Truco de 6? O capitão inscreve o trio por aqui: ${link}`);
  const mudar = (b: Record<string, unknown>) => {
    setOcupado(true);
    editarCampeonato(sessao, f.id, b).then(aoMudar, (e: Error) => avisarNaTela(e.message)).finally(() => setOcupado(false));
  };
  async function copiar() {
    try { await navigator.clipboard.writeText(link); setCopiado('Link copiado!'); } catch { setCopiado('Copie o link: ' + link); }
    medir('share', { method: 'link', content_type: 'campeonato' });
    setTimeout(() => setCopiado(null), 2500);
  }
  async function exportar() {
    try { const r = await exportarCampeonato(sessao, f.id); baixarCsv(r.arquivo, r.csv); } catch (e) { avisarNaTela((e as Error).message); }
  }
  return (
    <>
      <section aria-labelledby="h-divulgar" className="cartao organizar divulgar">
        <h2 id="h-divulgar" className="rotulo">Divulgar</h2>
        <p className="link-convite">{link}</p>
        <Qr texto={link} rotulo="QR code da página do campeonato" />
        <div className="acoes-convite">
          <a className="btn2 whats" href={zap} target="_blank" rel="noreferrer" onClick={() => medir('share', { method: 'whatsapp', content_type: 'campeonato' })}>WhatsApp</a>
          <button className="btn2" onClick={() => void copiar()}>Copiar link</button>
        </div>
        {copiado && <p className="ok" role="status">{copiado}</p>}
      </section>
      {!f.eu?.ferramentas ? (
        <div className="cadeado-tentos">
          <p className="cadeado">Chamar amigos, avisos, transmissão, página pública e exportar são ferramentas de organizador do plano Ouro. O campeonato segue até o fim.</p>
          <button className="btn2 dourado" onClick={() => ir({ tela: 'planos', plano: 'ouro', ciclo: 'mensal' })}>Ver planos</button>
        </div>
      ) : (
        <section aria-labelledby="h-ferramentas" className="ferramentas">
          <h2 id="h-ferramentas" className="rotulo">Ferramentas de organizador</h2>
          {f.estado === 'inscricao' && <button className="btn2 largo chamar-amigos" onClick={() => setFolha('amigos')}><IconeAmigos tamanho={20} />Chamar amigos</button>}
          {ativo && <button className="btn2 largo" onClick={() => setFolha('aviso')}>Mandar aviso aos inscritos</button>}
          <div className="campo">
            <span className="rotulo-campo">Transmissão</span>
            <Opcoes rotulo="Transmissão" classe="formatos" valor={f.transmissao} travado={ocupado || !ativo} aoMudar={(v) => mudar({ transmissao: v })}
              opcoes={[['nenhuma', 'Nenhuma'], ['final', 'Só a final'], ['todos', 'Todos os jogos']] as const} />
            <small>{f.transmissao === 'nenhuma'
              ? 'Assiste quem é amigo de alguém da mesa.'
              : `Qualquer conta Google assiste ${f.transmissao === 'final' ? 'a final' : 'os jogos'}, com ${ATRASO_DA_TRANSMISSAO_S} s de atraso. Vale para as salas abertas daqui em diante.`}</small>
          </div>
          <ul className="cartao regras-casa">
            <li><p><b>Página pública</b><span>{f.publico ? 'Aparece no Google e nos outros buscadores.' : 'Só quem tem o link vê. A página fica fora dos buscadores.'}</span></p>
              <Chave rotulo="Página pública nos buscadores" ligada={f.publico} travado={ocupado} aoMudar={(v) => mudar({ publico: v })} /></li>
          </ul>
          <button className="btn2 largo" onClick={() => void exportar()}>Exportar tabela e resultados (CSV)</button>
        </section>
      )}
      {folha === 'amigos' && <ChamarAmigosAoCampeonato f={f} sessao={sessao} aoFechar={() => setFolha(null)} />}
      {folha === 'aviso' && <AvisoAosInscritos f={f} sessao={sessao} aoFechar={() => setFolha(null)} />}
    </>
  );
}

function ChamarAmigosAoCampeonato({ f, sessao, aoFechar }: { f: FichaCampeonato; sessao: Sessao; aoFechar: () => void }) {
  const [amigos, setAmigos] = useState<AmigoNaLista[] | null>(null);
  const [escolhidos, setEscolhidos] = useState<Set<string>>(new Set());
  const [ocupado, setOcupado] = useState(false);
  useEffect(() => {
    const inscritos = new Set(f.trios.flatMap((t) => t.membros.map((m) => m.usuario)));
    lerAmigos(sessao).then((r) => setAmigos(r.amigos.filter((a) => !inscritos.has(a.usuario))), () => setAmigos([]));
  }, []);
  async function mandar() {
    setOcupado(true);
    try {
      const r = await convidarParaCampeonato(sessao, f.id, [...escolhidos]);
      avisarNaTela(r.enviados ? `${r.enviados} ${r.enviados === 1 ? 'convite enviado' : 'convites enviados'}.` : 'Esses amigos já tinham convite.');
      aoFechar();
    } catch (e) { avisarNaTela((e as Error).message); setOcupado(false); }
  }
  const todos = amigos?.length && escolhidos.size === amigos.length;
  return (
    <Folha titulo="Chamar amigos" aoFechar={aoFechar}>
      <p className="nota">Cada amigo recebe a notificação com o link do campeonato. Até {CONVITES_POR_DIA} convites por dia.</p>
      {!amigos && <p className="carregando">Carregando seus amigos...</p>}
      {amigos && amigos.length === 0 && <p className="nota">Todos os seus amigos já estão inscritos, ou você ainda não tem amigos no jogo.</p>}
      {amigos && amigos.length > 1 && (
        <button className="link" onClick={() => setEscolhidos(todos ? new Set() : new Set(amigos.map((a) => a.usuario)))}>{todos ? 'Tirar todos' : 'Escolher todos'}</button>
      )}
      {amigos && amigos.length > 0 && (
        <ul className="escolher-amigos">
          {amigos.map((a) => {
            const on = escolhidos.has(a.usuario);
            return (
              <li key={a.usuario}>
                <button role="checkbox" aria-checked={on} className={'cartao' + (on ? ' on' : '')}
                  onClick={() => setEscolhidos((s) => { const n = new Set(s); if (on) n.delete(a.usuario); else n.add(a.usuario); return n; })}>
                  <Rosto perfil={{ apelido: a.apelido, avatar: a.avatar }} usuario={a.usuario} cor="var(--gold-400)" tamanho={36} />
                  <b>{a.apelido}</b>
                  <span className="marca-escolha" aria-hidden="true">{on ? '✓' : ''}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <button className="btn1 largo" disabled={ocupado || escolhidos.size === 0} onClick={() => void mandar()}>
        {escolhidos.size ? `CHAMAR ${escolhidos.size}` : 'ESCOLHA OS AMIGOS'}
      </button>
      <button className="btn2 largo" onClick={aoFechar}>Fechar</button>
    </Folha>
  );
}

function AvisoAosInscritos({ f, sessao, aoFechar }: { f: FichaCampeonato; sessao: Sessao; aoFechar: () => void }) {
  const [texto, setTexto] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  async function mandar() {
    setErro(null);
    if (!texto.trim()) { setErro('Escreva o aviso.'); return; }
    if (textoOfensivo(texto)) { setErro('Escolha outro texto para o aviso.'); return; }
    setOcupado(true);
    try {
      const r = await avisarInscritos(sessao, f.id, texto.trim());
      avisarNaTela(`Aviso enviado para ${r.enviados} ${r.enviados === 1 ? 'pessoa' : 'pessoas'}.`);
      aoFechar();
    } catch (e) { setErro((e as Error).message); setOcupado(false); }
  }
  return (
    <Folha titulo="Aviso aos inscritos" aoFechar={aoFechar}>
      <label className="campo">
        <span>Mensagem</span>
        <textarea value={texto} maxLength={AVISO_MAX} rows={3} disabled={ocupado} placeholder="Sorteio da chave sexta às 20h." onChange={(e) => setTexto(e.target.value)} />
      </label>
      <small className="conta-letras">{[...texto].length}/{AVISO_MAX}</small>
      <small className="nota">Os 3 de cada trio inscrito recebem na lista de notificações e no celular. Até {AVISOS_POR_DIA} avisos por dia.</small>
      {erro && <p className="erro" role="alert">{erro}</p>}
      <button className="btn1 largo" disabled={ocupado} onClick={() => void mandar()}>MANDAR AVISO</button>
      <button className="btn2 largo" onClick={aoFechar}>Agora não</button>
    </Folha>
  );
}
